import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Small uppercase label above the title (prototype `.eyebrow`). */
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Accessible name when no visible `title` is rendered. */
  srTitle?: string;
  /** Width utility class for the panel (prototype default 520px). */
  widthClassName?: string;
  className?: string;
};

/** Right-anchored drawer, ported from the prototype's `.drawer` (520px, eyebrow
 *  + title header, bordered footer). Radix supplies focus-trap / a11y / motion. */
export function Drawer({
  open,
  onOpenChange,
  eyebrow,
  title,
  description,
  children,
  footer,
  srTitle = "Panel",
  widthClassName = "w-full sm:w-[520px] sm:max-w-[96vw]",
  className,
}: Props) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="vb-overlay z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "vb-root vb-drawer-panel fixed inset-y-0 right-0 z-50",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            widthClassName,
            className,
          )}
        >
          <div className="relative border-b border-vb-line px-[26px] pb-4 pt-5">
            {eyebrow && <div className="vb-eyebrow">{eyebrow}</div>}
            {title ? (
              <DialogPrimitive.Title className="m-0 mt-1 pr-10 text-[20px] font-bold leading-[1.2] tracking-[-0.012em] text-vb-text">
                {title}
              </DialogPrimitive.Title>
            ) : (
              <DialogPrimitive.Title className="sr-only">{srTitle}</DialogPrimitive.Title>
            )}
            {description && (
              <DialogPrimitive.Description className="mt-1 text-[12.5px] text-vb-text-2">
                {description}
              </DialogPrimitive.Description>
            )}
            <DialogPrimitive.Close
              className="absolute right-5 top-4 grid h-[30px] w-[30px] place-items-center rounded-vb-sm text-vb-text-3 transition-colors hover:bg-vb-bg-subtle hover:text-vb-text focus:outline-none focus-visible:ring-[3px] focus-visible:ring-vb-focus"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-[18px] overflow-y-auto overflow-x-hidden px-[26px] py-5">
            {children}
          </div>

          {footer && (
            <div className="flex items-center justify-end gap-[10px] border-t border-vb-line px-[26px] py-[14px]">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default Drawer;
